import { useLazyQuery, useMutation } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { useLocation } from '@reach/router';
import { useEffect, useState } from 'react';
import { FORM_PAGES } from '../components/Form/types';
import { vars } from '../reactive';
import { setFormPage } from '../reactive/actions';
import { clearLocationQueryParams } from '../utils';
import Notifier from '../utils/Notifier';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { GetUserProfile, UpdateUserInfo } from '../utils/queries';
import getStripe from '../utils/stripe';
import useCheckLocalStorageSupport from './useCheckLocalStorageSupport';

function useHandlePaymentMethodUpdates() {
  const location = useLocation();
  const [updateUserInfo] = useMutation(UpdateUserInfo);
  const [loading, setLoading] = useState<boolean>(false);
  const isLoggedIn = vars.isLoggedIn();
  const isLocalStorageSupported = useCheckLocalStorageSupport();
  const [getUserProfile] = useLazyQuery<{
    getProfile: Queries.CollarAPI_AppUser;
  }>(GetUserProfile, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    async function updateUserInfoPromise() {
      const stripe = await getStripe();
      const clientSecret = new URLSearchParams(location.search).get('setup_intent_client_secret')!;
      const userProfile = await getUserProfile();
      const payment = userProfile?.data?.getProfile?.payment;
      let paymentMethod = payment?.method;

      if (clientSecret) {
        const { setupIntent, error } = await stripe!.retrieveSetupIntent(clientSecret);
        if (error) {
          Bugsnag.notify({ name: 'Error in stripe.retrieveSetupIntent', message: JSON.stringify(error) });
          console.error(error.message);
          return;
        }
        clearLocationQueryParams(location);
        switch (setupIntent.status) {
          // case "succeeded":
          //   Notifier.success({
          //     title: "Payment successful",
          //     message: "Success! Your payment method has been saved.",
          //   });
          //   break;

          // case "processing":
          //   Notifier.info({
          //     title: "Payment processing",
          //     message: "Your payment is being processed.",
          //   });
          //   break;

          case 'requires_payment_method':
            // TODO: Redirect your user back to your payment page to attempt collecting
            // payment again
            Notifier.error({
              title: 'Payment error',
              message: 'Your payment has failed. Please try a different payment method.'
            });
            console.log('Payment failed');
            return setFormPage(FORM_PAGES.BOOKING_SELECT_PETS);
        }
        await updateUserInfo({
          variables: {
            payment: {
              ...payment,
              method: setupIntent?.payment_method
            }
          }
        });
        trackEvent(AnalyticsEvents.PAYMENT_METHOD_ADDED);
        paymentMethod = setupIntent?.payment_method;
      }
      if (vars.newOrders() && paymentMethod) {
        handleNewOrder();
      }
    }
    if (isLoggedIn && isLocalStorageSupported) {
      (async () => {
        setLoading(true);
        await updateUserInfoPromise();
        setLoading(false);
      })();
    }
  }, [isLoggedIn, isLocalStorageSupported]);

  useEffect(() => {
    if (!isLocalStorageSupported) {
      vars.formPage(FORM_PAGES.BROWSER_NOT_SUPPORTED);
    }
  }, [isLocalStorageSupported]);

  return loading;
}

export default useHandlePaymentMethodUpdates;

function handleNewOrder() {
  vars.formPage(FORM_PAGES.BOOKING_ORDER);
}
